// AdminGiftCardsDashboard.jsx
import { useState, useEffect } from 'react';
import { summaryApi } from 'common';
import { TrendingUp, Users, Search, Filter, DollarSign, Gift } from 'lucide-react';
import { useTheme } from "../../../components/Theme/ThemeProvider";
import { authFetch } from "../../../helpers/authFetch";

export function AdminGiftCardsDashboard() {
  const [stats, setStats] = useState(null);
  const [giftCards, setGiftCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    page: 1,
    limit: 20
  });
  const [searchTerm, setSearchTerm] = useState('');
  const { colors } = useTheme();

  useEffect(() => {
    fetchStats();
    fetchGiftCards();
  }, [filters]);

  const fetchStats = async () => {
    try {
      const response = await authFetch(summaryApi.getGiftCardStats.url, {
        method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
      });
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const fetchGiftCards = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        ...filters,
        ...(searchTerm && { search: searchTerm })
      });

      const response = await authFetch(`${summaryApi.getAllGiftCards.url}?${queryParams}`, {
       method: 'GET',
      
      headers: {
        'Content-Type': 'application/json'
      }
      });
      const data = await response.json();

      if (data.success) {
        setGiftCards(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch gift cards:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, page: 1 }));
    fetchGiftCards();
  };

  const cancelCard = async (cardId) => {
    if (!window.confirm('Are you sure you want to cancel this gift card?')) return;

    try {
      const url = summaryApi.cancelGiftCard.url.replace(':id', cardId);
      const response = await authFetch(url, {
        method: summaryApi.cancelGiftCard.method,
        
      headers: {
        'Content-Type': 'application/json'
      },
        body: JSON.stringify({
          reason: 'Cancelled by admin'
        })
      });

      const data = await response.json();
      if (data.success) {
        alert('Gift card cancelled successfully');
        fetchGiftCards();
      } else {
        alert(data.message || 'Failed to cancel gift card');
      }
    } catch (err) {
      alert('Error cancelling gift card');
    }
  };

  const StatCard = ({ icon: Icon, label, value, subValue, bgColor }) => (
    <div className="rounded-lg shadow p-6" style={{ backgroundColor: colors.bgSecondary }}>
      <div className="flex items-center justify-between mb-4">
        <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: bgColor }}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
      <p className="text-sm mb-1" style={{ color: colors.textSecondary }}>{label}</p>
      <p className="text-3xl font-bold" style={{ color: colors.text }}>{value}</p>
      {subValue && <p className="text-sm mt-1" style={{ color: colors.textSecondary }}>{subValue}</p>}
    </div>
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return { bg: `${colors.success}20`, text: colors.success };
      case 'redeemed': return { bg: `${colors.textSecondary}20`, text: colors.textSecondary };
      case 'expired': return { bg: `${colors.error}20`, text: colors.error };
      case 'cancelled': return { bg: `${colors.warning}20`, text: colors.warning };
      default: return { bg: `${colors.textSecondary}20`, text: colors.textSecondary };
    }
  };

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: colors.bgPrimary }}>
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-8" style={{ color: colors.text }}>Gift Cards Management</h1>

        {stats && (
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <StatCard
              icon={Gift}
              label="Total Gift Cards"
              value={stats.totalCards}
              bgColor={colors.primary}
            />
            <StatCard
              icon={TrendingUp}
              label="Active Cards"
              value={stats.activeCards}
              subValue={`₦${stats.totalActiveValue.toLocaleString()} in circulation`}
              bgColor={colors.success}
            />
            <StatCard
              icon={DollarSign}
              label="Total Sales"
              value={`₦${stats.totalSales.toLocaleString()}`}
              bgColor={colors.accent}
            />
            <StatCard
              icon={Users}
              label="Redeemed Cards"
              value={stats.redeemedCards}
              bgColor={colors.warning}
            />
          </div>
        )}

        <div className="rounded-lg shadow" style={{ backgroundColor: colors.bgSecondary }}>
          <div className="p-6 border-b" style={{ borderColor: colors.border }}>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3 w-5 h-5" style={{ color: colors.textSecondary }} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Search by code or email..."
                  className="w-full pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: colors.bgPrimary,
                    color: colors.text,
                    border: `1px solid ${colors.border}`,
                    '--tw-ring-color': colors.primary
                  }}
                />
              </div>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
                className="px-4 py-2 rounded-lg focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: colors.bgPrimary,
                  color: colors.text,
                  border: `1px solid ${colors.border}`,
                  '--tw-ring-color': colors.primary
                }}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="redeemed">Redeemed</option>
                <option value="expired">Expired</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead style={{ backgroundColor: colors.bgTertiary }}>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: colors.textSecondary }}>Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: colors.textSecondary }}>Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: colors.textSecondary }}>Balance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: colors.textSecondary }}>Recipient</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: colors.textSecondary }}>Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: colors.textSecondary }}>Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: colors.textSecondary }}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ 
                backgroundColor: colors.bgSecondary,
                borderColor: colors.border 
              }}>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin mx-auto"
                        style={{ borderColor: colors.primary, borderTopColor: 'transparent' }} />
                    </td>
                  </tr>
                ) : giftCards.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center" style={{ color: colors.textSecondary }}>
                      No gift cards found
                    </td>
                  </tr>
                ) : (
                  giftCards.map((card) => (
                    <tr key={card._id} style={{ 
                      backgroundColor: colors.bgSecondary,
                      borderColor: colors.border
                    }}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-mono text-sm" style={{ color: colors.text }}>{card.code}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" style={{ color: colors.text }}>
                        ₦{card.originalAmount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" style={{ color: colors.text }}>
                        ₦{card.currentBalance.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm">
                          <div className="font-medium" style={{ color: colors.text }}>{card.recipientName || 'N/A'}</div>
                          <div style={{ color: colors.textSecondary }}>{card.recipientEmail}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 rounded-full text-xs font-medium" style={{
                          backgroundColor: getStatusColor(card.status).bg,
                          color: getStatusColor(card.status).text
                        }}>
                          {card.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: colors.textSecondary }}>
                        {new Date(card.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {card.status === 'active' && (
                          <button 
                            onClick={() => cancelCard(card._id)}
                            style={{ color: colors.error }}
                            className="hover:opacity-80"
                          >
                            Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}